// src/pages/teacher/TeacherLayout.jsx
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { Navbar, Container, Dropdown, Form, Button } from "react-bootstrap";
import Swal from "sweetalert2";
import {
  motion, LazyMotion, domAnimation, AnimatePresence, useScroll, useSpring, useTransform
} from "framer-motion";
import { useAuth } from "../../context/AuthContext";
import { db } from "../../services/firebase";
import { doc, getDoc, setDoc, serverTimestamp } from "firebase/firestore";

/* ---------------- helpers ---------------- */
const cssVar = (name, fallback) =>
  typeof window !== "undefined"
    ? getComputedStyle(document.documentElement).getPropertyValue(name).trim() || fallback
    : fallback;
const clampHex = (v, f="#6a8cff") => (/^#([0-9a-f]{3}|[0-9a-f]{6})$/i.test(String(v||"").trim()) ? v : f);
const systemPrefersDark = () =>
  typeof window !== "undefined" &&
  window.matchMedia?.("(prefers-color-scheme: dark)")?.matches;

/* ── micro-animations + ripple ── */
const liVariants = {
  hidden: { opacity: 0, x: -6 },
  show: (i=0) => ({ opacity: 1, x: 0, transition: { delay: 0.02*i, duration: .25 } }),
  hover: { x: 2, transition: { type: "spring", stiffness: 240, damping: 18 } },
};
const spawnRipple = (e) => {
  const host = e.currentTarget;
  const r = document.createElement("span");
  const rect = host.getBoundingClientRect();
  const size = Math.max(rect.width, rect.height);
  r.className = "t-ripple";
  r.style.width = r.style.height = `${size}px`;
  r.style.left = `${e.clientX - rect.left - size/2}px`;
  r.style.top  = `${e.clientY - rect.top  - size/2}px`;
  host.appendChild(r);
  r.addEventListener("animationend", () => r.remove());
};

/* ---------------- 3D icon assets (Iconscout) ----------------
   Put your downloaded PNG/WebP in /public/assets/3d or replace with CDN URLs.
---------------------------------------------------------------- */
const ICONS_3D = {
  dashboard:   "/assets/3d/teacher.png",
  attendance:  "/assets/3d/attendance.png",
  exams:       "/assets/3d/exams.png",
  results:     "/assets/3d/results.png",
  assignments: "/assets/3d/assignments.png",
  video:       "/assets/3d/video.png",
  timetable:   "/assets/3d/timetable.png",
  syllabus:    "/assets/3d/syllabus.png",
  library:     "/assets/3d/library.png",
  clubs:       "/assets/3d/clubs.png",
  bus:         "/assets/3d/bus.png",
  calendar:    "/assets/3d/calendar.png",
  megaphone:   "/assets/3d/megaphone.png",
  courses:     "/assets/3d/courses.png",
  departments: "/assets/3d/departments.png",
  labs:        "/assets/3d/labs.png",
  research:    "/assets/3d/research.png",
  placements:  "/assets/3d/placements.png",
};

/* Preload 3D icons to avoid flashes */
function usePreloadIcons(map){
  useEffect(() => { Object.values(map).forEach(src => { const img = new Image(); img.src = src; }); }, [map]);
}

/* App glyph that prefers 3D image over Bootstrap icon */
function AppGlyph({ img, bi="bi-circle", size=18, alt="" }) {
  if (img) {
    return (
      <img
        src={img}
        alt={alt || ""}
        className="icon-3d"
        width={size+20}
        height={size+20}
        loading="lazy"
      />
    );
  }
  return <i className={`bi ${bi}`} style={{ fontSize: size }} />;
}

/* Build nav per institution type */
const buildNav = (kind="school") => {
  const common = [
    { to: "/teacher", icon: "bi-speedometer2", img: ICONS_3D.dashboard,   label: "Dashboard", end: true },
    { to: "/teacher/attendance", icon: "bi-clipboard-check", img: ICONS_3D.attendance, label: "Attendance" },
    { to: "/teacher/exams/examspage", icon: "bi-file-earmark-text", img: ICONS_3D.exams, label: "Exams" },
    { to: "/teacher/results-dashboard", icon: "bi-bar-chart-line", img: ICONS_3D.results, label: "Results" },
    { to: "/teacher/assignments", icon: "bi-journal-check", img: ICONS_3D.assignments, label: "Assignments" },
    { to: "/teacher/onlineclasses", icon: "bi-camera-video", img: ICONS_3D.video, label: "Online Classes" },
  ];
  const schoolOnly = [
    { sep: true, label: "Academics" },
    { to: "/teacher/timetable", icon: "bi-calendar2-week", img: ICONS_3D.timetable, label: "Timetable", disabled: true },
    { to: "/teacher/lesson-plans", icon: "bi-easel3", img: ICONS_3D.syllabus, label: "Lesson Plans", disabled: true },
    { to: "/teacher/syllabus", icon: "bi-book", img: ICONS_3D.syllabus, label: "Syllabus", disabled: true },
    { sep: true, label: "Activities" },
    { to: "/teacher/homework", icon: "bi-pencil-square", img: ICONS_3D.assignments, label: "Homework", disabled: true },
    { sep: true, label: "Services" },
    { to: "/teacher/library", icon: "bi-journal-richtext", img: ICONS_3D.library, label: "Library", disabled: true },
    { to: "/teacher/circulars", icon: "bi-megaphone", img: ICONS_3D.megaphone, label: "Circulars" },
  ];
  const collegeOnly = [
    { sep: true, label: "Academics" },
    { to: "/teacher/courses", icon: "bi-mortarboard", img: ICONS_3D.courses, label: "Courses", disabled: true },
    { to: "/teacher/departments", icon: "bi-diagram-3", img: ICONS_3D.departments, label: "Departments", disabled: true },
    { to: "/teacher/labs", icon: "bi-cpu", img: ICONS_3D.labs, label: "Lab Schedules", disabled: true },
    { to: "/teacher/research", icon: "bi-flask", img: ICONS_3D.research, label: "Research", disabled: true },
    { sep: true, label: "Career" },
    { to: "/teacher/seminars", icon: "bi-broadcast-pin", img: ICONS_3D.megaphone, label: "Seminars", disabled: true },
    { to: "/teacher/placements", icon: "bi-briefcase", img: ICONS_3D.placements, label: "Placements", disabled: true },
    { sep: true, label: "Resources" },
    { to: "/teacher/library", icon: "bi-journal-richtext", img: ICONS_3D.library, label: "Library", disabled: true },
    { to: "/teacher/circulars", icon: "bi-megaphone", img: ICONS_3D.megaphone, label: "Circulars" },
  ];
  const account = [
    { sep: true, label: "Account" },
    { to: "/teacher/profile", icon: "bi-person-circle", img: null, label: "Profile" },
    { to: "/teacher/support", icon: "bi-life-preserver", img: null, label: "Support" },
    { to: "/teacher/settings", icon: "bi-gear", img: null, label: "Settings" },
  ];
  return [...common, ...(kind === "college" ? collegeOnly : schoolOnly), ...account];
};

export default function TeacherLayout() {
  const { user, profile, logout } = useAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const teacherName =
    profile?.displayName || user?.displayName || user?.email?.split("@")[0] || "Teacher";

  /* Drawer */
  const [open, setOpen] = useState(false);
  useEffect(() => setOpen(false), [pathname]);
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    window.addEventListener("keydown", onKey, { passive: true });
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  /* School header + type */
  const schoolId = profile?.schoolId || localStorage.getItem("schoolId") || "";
  const [schoolName, setSchoolName] = useState("School");
  const [schoolLogo, setSchoolLogo] = useState("");
  const [instType, setInstType] = useState("school");
  useEffect(() => {
    let ignore = false;
    (async () => {
      if (!schoolId) return;
      try {
        const snap = await getDoc(doc(db, "schools", schoolId));
        if (snap.exists() && !ignore) {
          const d = snap.data() || {};
          setSchoolName(d.name || "School");
          setSchoolLogo(d.logoURL || d.logo || "");
          const t = String(d.type || d.category || "school").toLowerCase();
          setInstType(t.includes("college") ? "college" : "school");
        }
      } catch {}
    })();
    return () => { ignore = true; };
  }, [schoolId]);

  /* Theme + Accent */
  usePreloadIcons(ICONS_3D);
  const uid = user?.uid;
  const [accent, setAccent] = useState("#6a8cff");
  const applyAccent = useCallback((val) => {
    const c = clampHex(val);
    const root = document.documentElement;
    root.style.setProperty("--brand-accent", c);
    root.style.setProperty("--brand-accent-soft", "color-mix(in oklab, var(--brand-accent) 15%, transparent)");
  }, []);
  const [themeMode, setThemeMode] = useState("light");
  const [resolvedMode, setResolvedMode] = useState("light");
  const applyMode = useCallback((mode) => {
    const m = mode === "dark" ? "dark" : "light";
    document.documentElement.setAttribute("data-theme", m);
    setResolvedMode(m);
  }, []);
  useEffect(() => {
    const lsAccent = clampHex(localStorage.getItem("teacherAccentColor"));
    const rawMode  = localStorage.getItem("teacherThemeMode");
    const initial  = ["dark","light"].includes(rawMode) ? rawMode : (systemPrefersDark() ? "dark" : "light");
    setAccent(lsAccent); setThemeMode(initial); applyAccent(lsAccent); applyMode(initial);
  }, [applyAccent, applyMode]);

  const lastSavedRef = useRef({ accent: null, mode: null });
  const persistTimerRef = useRef(null);
  const persistTheme = useCallback((a,m) => {
    if (!uid) return;
    const last = lastSavedRef.current;
    if (last.accent === a && last.mode === m) return;
    clearTimeout(persistTimerRef.current);
    persistTimerRef.current = setTimeout(async () => {
      try {
        await setDoc(doc(db, "users", uid), { theme: { accent: a, mode: m }, updatedAt: serverTimestamp() }, { merge: true });
        lastSavedRef.current = { accent: a, mode: m };
      } catch {}
    }, 400);
  }, [uid]);
  useEffect(() => () => clearTimeout(persistTimerRef.current), []);
  useEffect(() => {
    let ignore = false;
    (async () => {
      if (!uid) return;
      try {
        const u = await getDoc(doc(db, "users", uid));
        if (ignore) return;
        const data = u?.data() || {};
        const a = clampHex(data?.theme?.accent || "");
        const m = ["light","dark"].includes(data?.theme?.mode) ? data.theme.mode : "";
        if (a) { setAccent(a); applyAccent(a); localStorage.setItem("teacherAccentColor", a); }
        if (m) { setThemeMode(m); applyMode(m); localStorage.setItem("teacherThemeMode", m); }
        lastSavedRef.current = { accent: a || localStorage.getItem("teacherAccentColor"), mode: m || localStorage.getItem("teacherThemeMode") };
      } catch {}
    })();
    return () => { ignore = true; };
  }, [uid, applyAccent, applyMode]);
  const changeAccent = (v) => { const c = clampHex(v); setAccent(c); applyAccent(c); localStorage.setItem("teacherAccentColor", c); persistTheme(c, themeMode); };
  const toggleThemeMode = () => setThemeMode(prev => { const next = prev==="light"?"dark":"light"; applyMode(next); localStorage.setItem("teacherThemeMode", next); persistTheme(accent, next); return next; });

  const brand = useMemo(() => ({ primary: cssVar("--brand-primary","#6a8cff"), accent: cssVar("--brand-accent",accent)||accent, mode: resolvedMode }), [accent, resolvedMode]);

  /* Logout */
  const doLogout = async () => {
    const res = await Swal.fire({
      title: "Log out?",
      text: "You’ll need to sign in again to access the teacher portal.",
      icon: "warning",
      showCancelButton: true,
      reverseButtons: true,
      confirmButtonText: "Yes, log me out",
      cancelButtonText: "Cancel",
      confirmButtonColor: brand.accent,
    });
    if (!res.isConfirmed) return;
    try { await logout?.(); } catch {}
    navigate("/login?role=teacher", { replace: true });
  };

  /* Topbar progress & shadow */
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 6);
    onScroll(); window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  const { scrollYProgress } = useScroll();
  const progress = useSpring(scrollYProgress, { stiffness: 100, damping: 20, mass: .2 });
  const progressScale = useTransform(progress, (v) => Math.max(.005, v));

  /* Nav based on school/college */
  const NAV = useMemo(() => buildNav(instType), [instType]);
  const PRESET_COLORS = ["#6a8cff","#0d6efd","#6610f2","#d63384","#dc3545","#fd7e14","#ffc107","#198754","#20c997","#0dcaf0","#6c757d","#343a40"];

  return (
    <LazyMotion features={domAnimation}>
      <div className={`t-shell ${open ? "drawer-open" : ""}`}>
        <motion.div className="t-progress" style={{ scaleX: progressScale }} aria-hidden />
        <div className="t-bg-waves" aria-hidden />

        {/* Sidebar / Drawer */}
        <aside id="t-nav" className={`t-sidebar ${open ? "open" : ""}`} aria-hidden={!open}>
          <div className="t-nav-header">
            <div className="d-flex align-items-center gap-2">
              {schoolLogo ? (
                <img src={schoolLogo} alt={schoolName || "School"} className="rounded-3 t-logo" style={{ width: 36, height: 36, objectFit: "cover" }} />
              ) : (
                <div className="rounded-3 d-inline-flex align-items-center justify-content-center t-logo-fallback">
                  {(schoolName || "S").slice(0,1).toUpperCase()}
                </div>
              )}
              <div className="lh-sm">
                <div className="fw-semibold text-truncate" title={schoolName || "School"}>{schoolName || "School"}</div>
                <div className="chip small">{instType === "college" ? "College" : "School"}</div>
              </div>
            </div>
          </div>

          <div className="t-user">
            <div className="t-avatar">{(teacherName || "T").slice(0,1).toUpperCase()}</div>
            <div className="flex-1">
              <div className="fw-semibold text-truncate">{teacherName}</div>
              <div className="small text-truncate">{user?.email || ""}</div>
            </div>
          </div>

          <nav className="t-nav-items">
            {NAV.map((n,i) =>
              n.sep ? (
                <div key={`sep-${i}`} className="mt-2 opacity-75 small px-2">{n.label}</div>
              ) : (
                <NavItem key={n.to||n.label} to={n.to} end={n.end} icon={n.icon} img={n.img} idx={i} disabled={n.disabled}>
                  {n.label}
                </NavItem>
              )
            )}
          </nav>

          <div className="t-nav-footer small text-muted px-1">
            <i className="bi bi-shield-check me-1" /> v1.2 • LearnSmart
          </div>
        </aside>

        {/* Drawer backdrop */}
        <AnimatePresence>
          {open && (
            <motion.div className="t-backdrop" onClick={() => setOpen(false)} initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} />
          )}
        </AnimatePresence>

        {/* Main */}
     <main className="t-main fancy">
  {/* ambient gradient blobs in the background */}
  <div className="t-ambient">
    <span className="blob b1" />
    <span className="blob b2" />
    <span className="blob b3" />
  </div>

  {/* Topbar (glassy) */}
  <Navbar className={`t-topbar glass ${scrolled ? "scrolled" : ""}`}>
    <Container fluid className="px-3">
      <div className="d-flex align-items-center gap-2">
        <button
          className="btn btn-sm btn-outline-secondary d-lg-none"
          onClick={() => setOpen(s => !s)}
          aria-label="Toggle menu"
        >
          <i className="bi bi-list" />
        </button>
        <AppGlyph img={ICONS_3D.dashboard} bi="bi-layout-sidebar" size={32} alt="" />
        <span className="fw-semibold">
          {instType === "college" ? "Faculty Portal" : "Teacher Portal"}
        </span>
      </div>

      <div className="d-flex align-items-center gap-2">
     

        {/* Theme toggle */}
        <Button
          size="sm"
          variant="outline-secondary"
          onClick={toggleThemeMode}
          title={`Switch to ${themeMode === "light" ? "dark" : "light"} mode`}
        >
          <i className={`bi ${themeMode === "dark" ? "bi-moon-stars" : "bi-brightness-high"}`} />
        </Button>

        {/* Color palette */}
        <Dropdown align="end" className="d-none d-md-inline-block">
          <Dropdown.Toggle size="sm" variant="outline-secondary" title="Theme color">
            <i className="bi bi-palette2 me-1" /> Color
          </Dropdown.Toggle>
          <Dropdown.Menu className="p-2" style={{ minWidth: 240 }}>
            <div className="swatch-grid mb-2">
              {PRESET_COLORS.map((c) => {
                const selected = (accent || "").toLowerCase() === c.toLowerCase();
                return (
                  <button
                    key={c}
                    type="button"
                    className={`color-swatch ${selected ? "selected" : ""}`}
                    style={{ backgroundColor: c }}
                    onClick={() => changeAccent(c)}
                    aria-label={`Pick ${c}`}
                  >
                    {selected ? <i className="bi bi-check-lg" /> : null}
                  </button>
                );
              })}
            </div>
            <div className="d-flex align-items-center gap-2">
              <span className="small text-muted">Custom</span>
              <Form.Control
                type="color"
                value={accent}
                onChange={(e) => changeAccent(e.target.value)}
                style={{ padding: 0, width: 44, height: 28 }}
              />
              <Form.Control
                size="sm"
                value={accent}
                onChange={(e) => changeAccent(e.target.value)}
                placeholder="#abcdef"
              />
            </div>
          </Dropdown.Menu>
        </Dropdown>

        {/* Tiny picker on small screens */}
        <Form.Control
          type="color"
          value={accent}
          onChange={(e) => changeAccent(e.target.value)}
          className="d-md-none"
          style={{ padding: 0, width: 36 }}
        />

        {/* Profile */}
        <Dropdown align="end">
          <Dropdown.Toggle size="sm" variant="outline-primary">
            <i className="bi bi-person-circle me-1" /> {teacherName}
          </Dropdown.Toggle>
          <Dropdown.Menu>
            <Dropdown.Item as={NavLink} to="/teacher/profile">
              <i className="bi bi-person me-2" /> Profile
            </Dropdown.Item>
            <Dropdown.Item as={NavLink} to="/teacher/settings">
              <i className="bi bi-gear me-2" /> Settings
            </Dropdown.Item>
            <Dropdown.Divider />
            <Dropdown.Item onClick={doLogout}>
              <i className="bi bi-box-arrow-right me-2" /> Sign out
            </Dropdown.Item>
          </Dropdown.Menu>
        </Dropdown>
      </div>
    </Container>
  </Navbar>

 
  {/* Elevated canvas for routed pages */}
  <div className="t-canvas">
    <div className="t-content">
      <Outlet />
    </div>
  </div>
</main>

        {/* Styles */}
        <style>{`
        /* ------- Modern background + glass ------- */
.t-main.fancy{
  position: relative;
  background:
    linear-gradient(180deg,
      color-mix(in oklab, var(--brand-accent) 12%, var(--bg)) 0%,
      color-mix(in oklab, var(--brand-accent) 6%, var(--bg)) 24%,
      var(--bg) 56%);
  min-height: 100vh;
}

/* soft ambient blobs */
.t-ambient{ position:absolute; inset:0; pointer-events:none; z-index:0; }
.t-ambient .blob{
  position:absolute; filter: blur(42px); opacity:.35;
  background: var(--brand-accent); border-radius: 50%;
}
.t-ambient .b1{ width:360px; height:360px; top:-120px; left:-120px; }
.t-ambient .b2{ width:280px; height:280px; right:-80px; top:-60px; background: color-mix(in oklab, var(--brand-accent) 65%, #ff9ad6); }
.t-ambient .b3{ width:220px; height:220px; bottom:120px; left:10%; background: color-mix(in oklab, var(--brand-accent) 55%, #79f2c0); }

/* frosted topbar */
.t-topbar.glass{
  background: color-mix(in oklab, var(--surface) 78%, transparent);
  border: 1px solid color-mix(in oklab, var(--border) 85%, transparent);
  backdrop-filter: blur(10px) saturate(1.1);
  border-radius: 14px;
  margin: 10px 14px 0;
}
.t-topbar.glass.scrolled{
  box-shadow: 0 10px 24px rgba(0,0,0,.12);
}

/* hero band */
.t-hero{
  position: relative; z-index: 1;
  margin: 12px 14px 0;
  border-radius: 18px;
  border: 1px solid color-mix(in oklab, var(--border) 70%, transparent);
  background:
    linear-gradient(140deg,
      color-mix(in oklab, var(--brand-accent) 22%, transparent) 0%,
      color-mix(in oklab, var(--brand-accent) 10%, transparent) 40%,
      color-mix(in oklab, var(--surface) 95%, transparent) 100%);
  display: grid;
  grid-template-columns: 1.2fr .8fr;
  gap: 10px;
  padding: 18px 18px;
  overflow: hidden;
}
.t-hero::after{
  content:""; position:absolute; inset:-10% -10% auto auto; height:160%; width:40%;
  background: radial-gradient(40% 50% at 70% 30%, color-mix(in oklab, #fff 20%, transparent), transparent 60%);
  pointer-events:none;
}
.t-hero .eyebrow{ font-size:12px; letter-spacing:.08em; text-transform:uppercase; color: var(--muted); }
.t-hero .title{ margin:.15rem 0 .1rem; font-weight:800; }
.t-hero .sub{ color: var(--muted); }
.t-hero-art{
  display:flex; align-items:center; justify-content:flex-end;
}
.t-hero-art img{
  width: 94px; height: 94px; object-fit: contain; border-radius: 16px;
  background: color-mix(in oklab, var(--brand-accent) 10%, transparent);
  box-shadow: 0 18px 36px color-mix(in oklab, var(--brand-accent) 18%, transparent);
  border: 1px solid color-mix(in oklab, var(--brand-accent) 35%, var(--border));
  padding: 6px;
}

/* elevated content canvas */
.t-canvas{
  position: relative; z-index: 1;
  margin: 10px 14px 18px;
  background: color-mix(in oklab, var(--surface) 98%, transparent);
  border: 1px solid var(--border);
  border-radius: 18px;
  box-shadow:
    0 8px 24px rgba(0,0,0,.10),
    0 1px 0 rgba(255,255,255,.12) inset;
}
.t-content{ padding: 18px; }
@media (max-width: 992px){
  .t-hero{ grid-template-columns: 1fr; }
  .t-hero-art{ justify-content:flex-start; }
  .t-content{ padding: 14px; }
}

/* keep existing sticky behavior tidy with rounded surfaces */
.t-topbar.glass + .t-hero{ margin-top: 8px; }

/* 3D icon styling */
.icon-3d{
  display:inline-block; object-fit: contain; border-radius: 10px;
  background: color-mix(in oklab, var(--brand-accent) 8%, transparent);
  box-shadow:
    0 10px 20px color-mix(in oklab, var(--brand-accent) 12%, transparent),
    0 1px 0 rgba(255,255,255,.18) inset;
  padding: 2px;
}

/* App grid */
.appgrid{ width: 320px; }
.appgrid .tile{ display:grid; grid-template-columns:repeat(3,1fr); gap:10px; padding:4px; }
.appgrid .tilebtn{
  border:1px solid var(--border); border-radius:12px; padding:10px 8px;
  background: color-mix(in oklab, var(--surface) 96%, transparent);
  display:flex; flex-direction:column; align-items:center; gap:6px; color:var(--text);
}
.appgrid .tilebtn:hover{ background: color-mix(in oklab, var(--brand-accent) 7%, transparent); }
.appgrid .tilebtn.disabled{ opacity:.6; cursor:not-allowed; }

/* Color swatches */
.swatch-grid{ display:grid; grid-template-columns:repeat(6,28px); gap:8px; }
.color-swatch{
  width:28px; height:28px; border-radius:8px; border:1px solid var(--border);
  display:inline-flex; align-items:center; justify-content:center;
  color:#fff; font-size:16px; line-height:1; cursor:pointer;
  transition: transform .08s ease, box-shadow .12s ease, border-color .12s ease;
}
.color-swatch:hover{ transform: translateY(-1px); box-shadow: 0 6px 14px rgba(0,0,0,.12); }
.color-swatch.selected{ outline: 2px solid color-mix(in oklab, var(--brand-accent) 55%, transparent); }
.color-swatch i{ filter: drop-shadow(0 1px 2px rgba(0,0,0,.35)); }

/* Theme tokens + bootstrap bridge */
:root{
  --brand-accent:${cssVar("--brand-accent", "#6a8cff")};
  --brand-accent-soft:${cssVar("--brand-accent-soft", "color-mix(in oklab, #6a8cff 15%, transparent)")};
  --accent-10: color-mix(in oklab, var(--brand-accent) 10%, transparent);
  --accent-18: color-mix(in oklab, var(--brand-accent) 18%, transparent);
  --accent-35: color-mix(in oklab, var(--brand-accent) 35%, transparent);
  --bg:#ffffff; --bg-soft:#f7f8fb; --surface:#ffffff; --text:#1f2330; --muted:#6c757d; --border:rgba(0,0,0,.12);
}
[data-theme="dark"]{
  --bg:#0b1220; --bg-soft:#0e1526; --surface:#10182c; --text:#e9eefc; --muted:#aeb7d6; --border:rgba(255,255,255,.12);
}
body{ background:var(--bg); color:var(--text); }
.t-shell{
  --bs-body-bg:var(--bg); --bs-body-color:var(--text); --bs-border-color:var(--border);
  --bs-secondary-color:var(--muted); --bs-primary:var(--brand-accent);
  --bs-link-color:var(--brand-accent); --bs-link-hover-color:color-mix(in oklab, var(--brand-accent) 86%, black);
  --bs-focus-ring-color:color-mix(in oklab, var(--brand-accent) 35%, transparent);
  --bs-card-bg:var(--surface); --bs-card-color:var(--text); --bs-card-border-color:var(--border);
  --bs-table-color:var(--text); --bs-table-bg:color-mix(in oklab, var(--surface) 98%, transparent);
  --bs-table-hover-bg:color-mix(in oklab, var(--brand-accent) 8%, transparent); --bs-table-border-color:var(--border);
  --bs-list-group-bg:color-mix(in oklab, var(--surface) 98%, transparent); --bs-list-group-color:var(--text);
  --bs-list-group-border-color:var(--border); --bs-form-control-bg:var(--surface);
  --bs-form-control-color:var(--text); --bs-form-control-border-color:var(--border);
  --bs-form-control-focus-border-color:color-mix(in oklab, var(--brand-accent) 65%, var(--border));
}
.t-shell .table thead th{ background:color-mix(in oklab, var(--surface) 96%, transparent); color:var(--text); border-bottom-color:var(--border); }
.t-shell .badge.bg-light{ background-color:var(--bs-table-bg)!important; color:var(--text)!important; border:1px solid var(--border); }

/* global fx */
.t-bg-waves{
  position:fixed; inset:0; z-index:0; pointer-events:none;
  background:
    radial-gradient(60rem 22rem at -15% -10%, var(--accent-10), transparent 60%),
    radial-gradient(60rem 22rem at 115% -10%, var(--accent-18), transparent 60%);
}
.t-shell>*{ z-index:1; }
.t-progress{
  position:fixed; inset-inline-start:0; top:0; height:3px; transform-origin:0% 50%;
  background:linear-gradient(90deg, var(--accent-18), var(--brand-accent)); z-index:2000;
}

/* layout + nav */
.t-shell{ display:flex; min-height:100vh; background:var(--bg); color:var(--text); }
.t-sidebar{
  width:272px; flex:0 0 272px; padding:12px; display:flex; flex-direction:column; gap:12px;
  background:color-mix(in oklab, var(--surface) 96%, transparent);
  border-right:1px solid var(--border); position:sticky; top:0; height:100vh; z-index:10;
  backdrop-filter: blur(10px);
}
.t-logo{ border:1px solid var(--border); }
.t-logo-fallback{
  width:36px; height:36px; font-weight:700; background:var(--accent-10); border:1px solid var(--accent-35);
  color:var(--brand-accent); display:inline-flex; align-items:center; justify-content:center;
}
.chip{
  display:inline-block; padding:.2rem .5rem; border-radius:.6rem; border:1px solid var(--accent-35);
  background: color-mix(in oklab, var(--brand-accent) 10%, transparent); color: var(--brand-accent); font-weight:600;
}
.t-nav-header{ padding:6px 6px 2px 6px; }
.t-user{
  display:flex; align-items:center; gap:10px; padding:10px; border:1px solid var(--border);
  border-radius:14px; background:color-mix(in oklab, var(--surface) 96%, var(--accent-10));
}
.t-avatar{
  width:34px; height:34px; border-radius:10px; font-weight:700; display:flex; align-items:center; justify-content:center;
  border:1px solid var(--accent-35); color: var(--brand-accent);
}
.t-nav-items{ display:flex; flex-direction:column; gap:8px; padding-top:4px; }
.t-nav-link{ text-decoration:none; }
.t-nav-item{
  position:relative; overflow:hidden; border:1px solid var(--border);
  background: color-mix(in oklab, var(--surface) 96%, transparent);
  border-radius:14px; padding:10px; display:flex; align-items:center; gap:10px; color:var(--text);
  transition: background .15s ease, border-color .15s ease, transform .1s ease, color .15s ease, box-shadow .2s ease;
}
.t-nav-item .t-rail{
  position:absolute; left:6px; top:18%; height:64%; width:4px; border-radius:6px;
  background:transparent; transition:background .18s ease, width .18s ease;
}
.t-nav-item .soon{ margin-left:auto; font-size:.72rem; padding:.1rem .4rem; border-radius:.5rem; border:1px solid var(--border); }
.t-nav-item .t-shine{
  position:absolute; inset:0; pointer-events:none;
  background: linear-gradient(90deg, transparent, color-mix(in oklab, #fff 14%, transparent), transparent);
  transform: translateX(-120%); opacity:0; transition: opacity .2s ease;
}
.t-nav-item:hover .t-shine{ animation: tShineMove 1.2s ease forwards; opacity:.75; }
@keyframes tShineMove { to { transform: translateX(120%); } }
.t-nav-item:hover{ background: color-mix(in oklab, var(--brand-accent) 7%, transparent); transform: translateY(-1px); border-color: var(--accent-35); }
.t-nav-link.active .t-nav-item{
  box-shadow: inset 0 0 0 1px var(--accent-10), 0 6px 18px -12px var(--brand-accent);
  border-color: var(--accent-35); background: color-mix(in oklab, var(--brand-accent) 12%, transparent);
}
.t-nav-link.active .t-nav-item .t-rail{ background: var(--brand-accent); width: 6px; }

/* ripple */
.t-ripple{ position:absolute; border-radius:50%; transform: scale(0);
  background: color-mix(in oklab, var(--brand-accent) 25%, transparent); animation: tRipple 700ms ease-out; pointer-events:none; opacity:.6; }
@keyframes tRipple { to { transform: scale(1); opacity: 0; } }

.t-nav-footer{ margin-top:auto; padding:4px; }

/* drawer */
@media (max-width: 992px){
  .t-sidebar{
    position: fixed; inset: 0 auto 0 0; height: 100dvh; transform: translateX(-100%);
    transition: transform .28s cubic-bezier(.2,.8,.2,1.02); overflow-y:auto; overscroll-behavior:contain;
    padding-bottom: max(12px, env(safe-area-inset-bottom)); background: color-mix(in oklab, var(--surface) 96%, transparent);
    backdrop-filter: blur(8px);
  }
  .t-sidebar.open{ transform: translateX(0); }
  .t-backdrop{ position: fixed; inset: 0; background: rgba(0,0,0,.35); z-index:9; }
  .t-shell.drawer-open{ overflow: hidden; }
}

/* main */
.t-main{ flex:1; display:flex; flex-direction:column; position:relative; z-index:1; background:var(--bg); }
.t-topbar{
  height:52px; display:flex; align-items:center; padding:0; border-bottom:1px solid var(--border);
  position: sticky; top:0; z-index:5; background: color-mix(in oklab, var(--surface) 92%, transparent);
  backdrop-filter: blur(6px); transition: box-shadow .2s ease, background .2s ease, border-color .2s ease;
}
.t-topbar.scrolled{ box-shadow: 0 .25rem .8rem rgba(0,0,0,.12); background: color-mix(in oklab, var(--surface) 96%, transparent); }
.t-content{ padding: 16px; }
@media (max-width: 992px){ .t-content{ padding:12px; } }

/* reduced motion */
@media (prefers-reduced-motion: reduce){ .t-nav-item, .t-topbar, .color-swatch, .tilebtn{ transition: none !important; } }
        `}</style>
      </div>
    </LazyMotion>
  );
}

/* ---------- sidebar nav item ---------- */
function NavItem({ to, icon, img, end, children, idx=0, disabled }) {
  const glyph = <AppGlyph img={img} bi={icon} size={18} alt="" />;
  if (disabled) {
    return (
      <div className="t-nav-link">
        <motion.div className="t-nav-item" variants={liVariants} custom={idx} initial="hidden" animate="show" whileHover="hover" onMouseDown={spawnRipple} role="button" aria-disabled="true">
          <span className="t-rail" aria-hidden />
          {glyph}
          <span className="label">{children}</span>
          <span className="soon">Soon</span>
          <span className="t-shine" aria-hidden />
        </motion.div>
      </div>
    );
  }
  return (
    <NavLink to={to} end={end} className={({ isActive }) => `t-nav-link ${isActive ? "active" : ""}`}>
      <motion.div className="t-nav-item" variants={liVariants} custom={idx} initial="hidden" animate="show" whileHover="hover" onMouseDown={spawnRipple}>
        <span className="t-rail" aria-hidden />
        {glyph}
        <span className="label">{children}</span>
        <span className="t-shine" aria-hidden />
      </motion.div>
    </NavLink>
  );
}

/* ---------- app launcher tile ---------- */
function AppTile({ icon, img, label, to, disabled }) {
  const glyph = <AppGlyph img={img} bi={icon} size={20} alt="" />;
  if (disabled) {
    return (
      <div className="tile">
        <div className="tilebtn disabled">{glyph}<small>{label}</small></div>
      </div>
    );
  }
  return (
    <NavLink to={to} className="tile">
      <div className="tilebtn">{glyph}<small>{label}</small></div>
    </NavLink>
  );
}
